import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useFormik } from 'formik';
import _, { isEqual } from 'lodash';
import { useRecoilValue } from 'recoil';

import { getClinicalNote } from 'api/ClinicalNote';
import { createForm } from 'api/Forms';

import { wizardTemplateQueryKey } from 'lib/constants/queries/queryKeys';
import { spaceToKebabCase } from 'lib/helpers/utility';
import useDisplaySettings from 'lib/hooks/useDisplaySettings';

import {
  FormInitialValues,
  FormValidationSchema
} from 'components/practice/Settings/components/Forms/FormBuilder/lib/helpers';
import { processCustomFormTypes } from 'components/practice/charts/ClinicalNote/lib/processCustomNoteTypes';
import { showAlert } from 'components/shared/Alert/Alert';
import { permissions, userState as user_state } from 'components/state';

import { useClinicalNoteContext } from '../ClinicalNoteContext/ClinicalNoteContext';

import { FormBuilderContext } from './FormBuilderContext';

export const FormBuilderContextProvider = ({ children }) => {
  const userState = useRecoilValue(user_state);
  const [form, setForm] = useState({});
  const [openModal, setOpenModal] = useState(false);
  const { is_admin } = useRecoilValue(permissions);
  const [autoSave, setAutoSave] = useState(
    userState?.display_settings?.formBuilder?.auto_save?.enabled
  );
  const [saveButtonText, setSaveButtonText] = useState('Save Progress');
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm);
  const [isEdit, setIsEdit] = useState(false);
  const [showNavigationButtons, setShowNavigationButtons] = useState(false);
  const [viewFormModal, setViewFormModal] = useState(false);
  const [packetId, setPacketId] = useState();
  const [isSaveAndExit, setIsSaveAndExit] = useState(false);

  const [selectedLength, setSelectedLength] = useState(0);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const location = useLocation();
  const { id, appointmentId } = useParams();

  const { setAdvancedHP, setAdvancedSOAP, setCustomFormTypes } = useClinicalNoteContext() || {};

  // differentiate between admin and patient portal id param
  const practiceId = !appointmentId ? id : null;

  const initialBreadCrumbs = [
    { title: 'Name&Type', view: 'name_and_type', active: true, isFinished: false },
    { title: 'Builder', view: 'builder', active: false, isFinished: false },
    { title: 'Review', view: 'review', active: false, isFinished: false },
    {
      title: 'AssignToPacket',
      view: 'assign_to_packet',
      active: false,
      isFinished: false,
      hidden: true
    }
  ];

  const [breadCrumbs, setBreadCrumbs] = useState(initialBreadCrumbs);
  const [enableNextTo, setEnableNextTo] = useState(false);
  const getActiveBreadCrumb = breadCrumbs.find((breadCrumb) => breadCrumb.active === true);

  const { mutateAsync: mutateCreateForm, isLoading: isFormSavingLoading } = useMutation({
    mutationFn: (data) => createForm(navigate, data)
  });

  const { saveDisplaySettings } = useDisplaySettings(userState);

  const updateCNForms = async () => {
    try {
      const res = await getClinicalNote(navigate, {
        patientId: id,
        appointmentId,
        exporting: false,
        forPractice: true
      });
      const { advancedHP, advancedSOAP, customFormTypes, redirect } = res;

      if (redirect) navigate(redirect);

      if (advancedHP)
        setAdvancedHP(
          advancedHP.map((item) => ({
            ...item,
            custom_title: `${spaceToKebabCase(item?.form?.name || item?.name)}-${
              item?.form_id || item?.id
            }`
          }))
        );

      if (advancedSOAP) {
        setAdvancedSOAP(
          advancedSOAP.map((item) => ({
            ...item,
            custom_title: `${spaceToKebabCase(item?.form?.name || item?.name)}-${
              item?.form_id || item?.id
            }`
          }))
        );
      }

      if (customFormTypes) {
        const processedCustomFormTypes = processCustomFormTypes({ customFormTypes });

        setCustomFormTypes(processedCustomFormTypes);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const formik = useFormik({
    initialValues: FormInitialValues(),
    validationSchema: FormValidationSchema,
    onSubmit: async (values) => {
      let formPacketSubmit = false;
      if (getActiveBreadCrumb.view === 'assign_to_packet' || isEdit || isSaveAndExit) {
        formPacketSubmit = true;
      }
      await mutateCreateForm(
        {
          ...values,
          formPacketSubmit,
          isEdit,
          step: getActiveBreadCrumb?.view,
          practice_id: practiceId
        },
        {
          onSuccess: async ({ code, form, packetIds, error }) => {
            if (code === 0) {
              queryClient.invalidateQueries(['forms']);
              queryClient.invalidateQueries([wizardTemplateQueryKey]);
              queryClient.invalidateQueries(['wizardDynamicSteps']);
              queryClient.refetchQueries(['customForm']);
              queryClient.refetchQueries(['form_packets']);
              queryClient.refetchQueries(['form_drafts']);
              showAlert({
                title: isEdit
                  ? 'Form updated successfully'
                  : isSaveAndExit
                    ? 'Form saved successfully'
                    : 'Form created successfully',
                color: 'success'
              });

              if (appointmentId) {
                await updateCNForms();
              }
            } else {
              showAlert({
                title: isEdit ? 'Form update failed' : 'Form creation failed',
                message: error ?? 'Something went wrong',
                color: 'danger'
              });
            }
            formik.setValues({ ...formik.values, form, form_packet_ids: packetIds });
            setPrevFormValues(form);
            setPrevPacketIds(packetIds);
            handleClose();
          }
        }
      );
    }
  });

  const [prevFormValues, setPrevFormValues] = useState(formik?.values);
  const [prevPacketIds, setPrevPacketIds] = useState(formik.values?.form_packet_ids);

  useEffect(() => {
    const handler = _.debounce(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    handler();

    return () => {
      handler.cancel();
    };
  }, [searchTerm]);

  useEffect(() => {
    const intervalId = setInterval(async () => {
      if (!openModal) return;
      if (!autoSave || isFormSavingLoading) return;
      if (isEqual(prevFormValues, formik.values.form)) return;
      if (getActiveBreadCrumb.view === 'assign_to_packet') {
        if (isEqual(prevPacketIds, formik.values.form_packet_ids)) return;
      }
      const formPacketSubmit = isEdit;

      await mutateCreateForm(
        {
          ...formik.values,
          step: getActiveBreadCrumb?.view,
          practice_id: practiceId,
          formPacketSubmit,
          isEdit
        },
        {
          onSuccess: ({ code, form, packetIds, error }) => {
            queryClient.refetchQueries(['forms']);
            queryClient.refetchQueries(['customForm']);
            queryClient.refetchQueries(['form_drafts']);
            queryClient.invalidateQueries([wizardTemplateQueryKey]);
            queryClient.invalidateQueries(['wizardDynamicSteps']);
            if (code !== 0) {
              showAlert({
                title: 'Form creation failed',
                message: error ?? 'Something went wrong',
                color: 'danger'
              });
              setAutoSave(false);
            }
            formik.setValues({ ...formik.values, form, form_packet_ids: packetIds });
            setPrevFormValues(form);
            setPrevPacketIds(packetIds);
          }
        }
      );
    }, 30000); // 30s

    return () => {
      clearInterval(intervalId);
    };
  }, [autoSave, formik.values]);

  useEffect(() => {
    if (location.state?.openModal) {
      setOpenModal(true);
    }
    if (location.state?.packet_id) {
      setPacketId(location.state?.packet_id);
    }
  }, [location.state, setOpenModal]);

  useEffect(() => {
    if (is_admin) {
      setAutoSave(true);
    }
  }, [is_admin]);

  const handleClose = () => {
    setOpenModal(false);
    setBreadCrumbs(initialBreadCrumbs);
    formik.resetForm();
    setEnableNextTo(false);
    setSaveButtonText('Save Progress');
    setIsEdit(false);
    setShowNavigationButtons(false);
    setIsSaveAndExit(false);
  };

  const formChange = (event) => {
    formik.setFieldValue('form.json', { fields: event });
  };

  const handleSave = async () => {
    let formPacketSubmit = false;
    if (isEdit || isSaveAndExit) {
      formPacketSubmit = true;
    }
    await mutateCreateForm(
      {
        ...formik.values,
        step: getActiveBreadCrumb?.view,
        practice_id: practiceId,
        formPacketSubmit,
        isEdit
      },
      {
        onSuccess: ({ code, form, packetIds, error }) => {
          queryClient.refetchQueries(['forms']);
          queryClient.refetchQueries(['customForm']);
          queryClient.refetchQueries(['form_drafts']);
          queryClient.invalidateQueries([wizardTemplateQueryKey]);
          queryClient.invalidateQueries(['wizardDynamicSteps']);
          if (code !== 0) {
            showAlert({
              title: 'Form creation failed',
              message: error ?? 'Something went wrong',
              color: 'danger'
            });
            setAutoSave(false);
          }
          formik.setValues({ ...formik.values, form, form_packet_ids: packetIds });
          setPrevFormValues(form);
          setPrevPacketIds(packetIds);
        }
      }
    );
  };

  const handleAutoSave = (event) => {
    setAutoSave(event);
    saveDisplaySettings({
      ...userState.display_settings,
      formBuilder: {
        auto_save: {
          enabled: event
        }
      }
    });
  };

  return (
    <FormBuilderContext.Provider
      value={{
        autoSave,
        setAutoSave,
        saveButtonText,
        setSaveButtonText,
        breadCrumbs,
        setBreadCrumbs,
        initialBreadCrumbs,
        formik,
        enableNextTo,
        setEnableNextTo,
        setOpenModal,
        openModal,
        handleClose,
        formChange,
        form,
        setForm,
        searchTerm,
        setSearchTerm,
        debouncedSearchTerm,
        setDebouncedSearchTerm,
        isFormSavingLoading,
        handleSave,
        practiceId,
        isEdit,
        setIsEdit,
        showNavigationButtons,
        setShowNavigationButtons,
        viewFormModal,
        setViewFormModal,
        handleAutoSave,
        packetId,
        setPacketId,
        selectedLength,
        setSelectedLength,
        isSaveAndExit,
        setIsSaveAndExit
      }}>
      {children}
    </FormBuilderContext.Provider>
  );
};
